/**
 * AiReliabilityPanel — where the statistical engine is actually right, by symbol.
 *
 * WHY IT SURVIVED THE MERGE
 *
 * The Edge board's aggregate is not worth acting on: 1,492 resolved picks, direction accuracy 18%
 * against its own 17% baseline. But an aggregate of 1,492 rows can hide real structure, and this
 * table is where it would be — a symbol at 80% over five picks is noise, one at 71% over a hundred
 * would not be. Keeping the breakdown while dropping the board is the honest reading of that
 * evidence: the average does not justify acting, and the average is not the only thing measured.
 *
 * It sits on the Performance tab beside the model layer's own per-grade and per-direction numbers,
 * so both "did the AI add value" questions are answered in one place instead of on two pages.
 *
 * The headline row is deliberately blunt. An engine whose edge over its own baseline is inside the
 * noise should say so above the table, not let a column of green hit-rates imply otherwise.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Tooltip, CircularProgress, Table, TableHead, TableRow,
  TableCell, TableBody, Alert as MuiAlert,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAppTheme } from '../../hooks/useAppTheme';
import { edgeService } from '../../services/edge.service';

const pctv = (v: number | null | undefined) =>
  v == null ? '—' : `${Math.round(v * 100)}%`;

const AiReliabilityPanel: React.FC = () => {
  const { C } = useAppTheme();
  const [track, setTrack] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    edgeService.getTrackRecord('NEXT_DAY')
      .then((r: any) => setTrack(r?.data?.data ?? null))
      .catch(() => setTrack(null))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>;
  if (!track?.resolved) return null;

  const edge = track.edgeVsBase;
  // Percentage-point edge against the standard error of a proportion at this n. Below ~1 SE the
  // difference is not distinguishable from chance, and saying so is the entire value of the panel.
  const n = Number(track.resolved) || 0;
  const p = Number(track.baseRate) || 0.5;
  const sePp = n > 0 ? Math.sqrt((p * (1 - p)) / n) * 100 : null;
  const edgePp = edge != null ? edge * 100 : null;
  const inNoise = edgePp != null && sePp != null && Math.abs(edgePp) < sePp;

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          The statistical engine, by symbol
        </Typography>
        <Tooltip arrow title="The other AI in this app: a self-trained ranker that learns which indicator conditions precede a good move. It does not feed the board — RockBand does — and this is the record of how often its own directional calls were right.">
          <InfoOutlinedIcon sx={{ fontSize: 14, color: C.dim, cursor: 'help' }} />
        </Tooltip>
      </Box>

      <MuiAlert severity={inNoise ? 'warning' : 'info'} variant="outlined" sx={{ mb: 1.2, py: 0.3 }}>
        <Typography variant="caption" sx={{ lineHeight: 1.5 }}>
          Over <b>{n.toLocaleString('en-IN')}</b> resolved picks it called the direction correctly{' '}
          <b>{pctv(track.dirAccuracy)}</b> of the time against a baseline of{' '}
          <b>{pctv(track.baseRate)}</b> — an edge of{' '}
          <b>{edgePp == null ? '—' : `${edgePp >= 0 ? '+' : ''}${edgePp.toFixed(1)} pp`}</b>.
          {sePp != null && (
            <> At this sample the standard error is about {sePp.toFixed(1)} pp, so{' '}
              {inNoise
                ? 'that difference is inside the noise and is not evidence of an edge.'
                : 'the difference is larger than chance alone would produce.'}
            </>
          )}
          {' '}<b>{pctv(track.flatRate)}</b> of picks produced no move at all.
        </Typography>
      </MuiAlert>

      {!!track.perSymbol?.length && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  ['Symbol', 'The instrument the picks were made on.', 'left'],
                  ['Picks', 'Resolved picks for this symbol. Minimum four to appear at all — and four is still far too few to act on.', 'right'],
                  ['Hit-rate', 'How often the direction was right for this symbol. Green means above the engine’s overall baseline, which is a low bar.', 'right'],
                  ['Avg move', 'Average move in the direction called. A high hit-rate on tiny moves is not the same as a profitable one.', 'right'],
                ].map(([h, hint, align]) => (
                  <Tooltip key={h as string} arrow title={hint as string}>
                    <TableCell align={align as any} sx={{ fontWeight: 800, whiteSpace: 'nowrap', cursor: 'help' }}>{h}</TableCell>
                  </Tooltip>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {track.perSymbol.map((s: any, i: number) => (
                <TableRow key={i} sx={{ opacity: (s.n ?? 0) < 10 ? 0.6 : 1 }}>
                  <TableCell sx={{ fontWeight: 700 }}>{s.symbol}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.72rem' }}>{s.n}</TableCell>
                  <TableCell align="right" sx={{
                    fontSize: '0.72rem', fontWeight: 800,
                    color: (s.hitRate ?? 0) >= (track.baseRate ?? 0.5) ? C.bull : C.bear,
                  }}>{pctv(s.hitRate)}</TableCell>
                  <TableCell align="right" sx={{
                    fontSize: '0.72rem', color: (s.avgReturn ?? 0) >= 0 ? C.bull : C.bear,
                  }}>
                    {(s.avgReturn ?? 0) >= 0 ? '+' : ''}{(s.avgReturn ?? 0).toFixed(2)}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="caption" sx={{ color: C.dim, display: 'block', mt: 0.6 }}>
            Rows with fewer than ten picks are dimmed — at that sample a hit-rate is mostly noise,
            and the eye reads a column of green as a finding whether or not it is one.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AiReliabilityPanel;
